import sendRequest from './sendRequest';
import { getAdobeData } from './adobeUtils';
import { THROTTLE_RATE } from './constants';
import logger from './logger'
import { EventPayloadSansAdobe, addAdobeData } from './structureData';
import stateManager from './stateManager';

// Though the response structure is currently known, we don't want anyone to start using it now, as it may change in the future.
// Marking it as `unknown` will prohibit usage.
export type WtaEndpointResponse = { data: unknown } | Error;

type Throttler = {
    queueEvent: (EventData: EventPayloadSansAdobe, force?: boolean) => Promise<WtaEndpointResponse | Error>
    popCache: () => EventPayloadSansAdobe[];
    eventCache: EventPayloadSansAdobe[];
    startCountdown: () => void;
    sendCachedEvents: () => Promise<WtaEndpointResponse | undefined>;
    resetCountdown: () => void; // for unit tests
};

const throttler = {} as Throttler;

// Export eventCache for testing purposes:
throttler.eventCache = [];

// Return a promise that resolves when the event is finally delivered:
throttler.queueEvent = async (eventSpecs, force = false) => {
    // Cache event and hold for bulk send:
    throttler.eventCache.push(eventSpecs);
    try {
        let sendPromise
        if (force) {
            sendPromise = throttler.sendCachedEvents();
        } else {
            sendPromise = throttler.startCountdown();
        }
        return sendPromise;
    } catch (e) {
        logger.error(e);
        return e;
    }
}

// This module's local state:
let runningTimeout: number | undefined;
let countdownPromise: Promise<WtaEndpointResponse> | undefined;
let countdownResolve: (result: WtaEndpointResponse) => void;
// Clear out all state from timer:
throttler.resetCountdown = () => {
    clearTimeout(runningTimeout)
    runningTimeout = undefined;
    countdownPromise = undefined;
};

throttler.startCountdown = () => {
    // Ensure only one countdown exists at a time. This single promise will be returned to
    // all callers of client.event, and will resolve when the endpoint has responded to the
    // bulk insert request.
    countdownPromise = countdownPromise || new Promise((resolve, reject) => {
        // A forced send bypasses `startCountdown`, going right to `sendCachedEvents`.
        // This means the promise that was created by startCountdown still needs to be resolved
        // Thus, we need to hoist the resolve function, so it can be called externally.
        countdownResolve = resolve;
        runningTimeout = window.setTimeout(() => {
            throttler.sendCachedEvents()
                .then(resolve)
                .catch(reject)
        }, THROTTLE_RATE);
    });
    return countdownPromise;
};

throttler.popCache = () => throttler.eventCache.splice(0, throttler.eventCache.length);

throttler.sendCachedEvents = async () => {
    // Grab data coming in from Adobe's endpoint:
    const adobeIds = await getAdobeData();
    // If `sendCachedEvents` was called directly (instead of through `startCountdown`), the countdown
    // should be shut off to avoid excess calls
    throttler.resetCountdown();
    // Clear cache, moving contents to array we'll be sending to endpoint:
    const cacheContents = throttler.popCache();
    // Add Adobe data:
    const eventsWithAdobeData = cacheContents.map(payload => addAdobeData(payload, adobeIds));
    // In the case of a prior force send, there may be nothing left to relay in the cache.
    // In this case, all awaiting promises will have been resolved, and we can avoid another
    // call to the endpoint.
    let result;
    if (eventsWithAdobeData.length) {
        // Get staging or prod endpoint:
        const url = stateManager.get().endpoint;
        const endpointOptions = stateManager.get().endpointOptions;
        // Send to analytics endpoint:
        result = await sendRequest({ url, data: eventsWithAdobeData, options: endpointOptions }) as WtaEndpointResponse;
        // If any other event calls were waiting on
        // responses from the endpoint resolve those now:
        if (countdownResolve) {
            countdownResolve(result);
        }
    }
    // Relay response back to caller:
    return result;
};

export default throttler;
